import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, Pressable, Platform,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { CameraView, useCameraPermissions } from 'expo-camera';
import Modal from 'react-native-modal';
import NfcManager, { NfcTech } from 'react-native-nfc-manager';
import Colors from '../../constants/Colors';
import InfoIcon from '../../components/30pxIcons/InfoIcon';
import Style from '../../constants/Style';
import TabButton from '../../components/TabButton';
import Button from '../../components/Button';
import FilterIcon16 from '../../components/16pxIcons/FilterIcon';
import SearchBar from '../../components/SearchBar';
import AssetList from './components/AssetList';
import {
  AssetInstance, UserPermission, Template, TemplateType,
} from '../../constants/Types';
import AssetFilterModal from './components/AssetFilterModal';
import AlertModal from '../../components/AlertModal';
import { screenMaxWidth } from '../../components/TopSection';
import SelectedAsset from './components/SelectedAsset';
import PressableStyled, { PressableState } from '../../components/PressableStyled';
import getTemplateGroups from '../../requests/Assets/GetTemplateGroups';
import isMobile from '../../components/IsMobile';
import PlusIcon from '../../components/16pxIcons/PlusIcon';
import AddAssetModal from '../../components/AssetManagement/AddAssetModal';
import TabSelectionMobile from '../../components/TabSelectionMobile';
import ScanIcon16 from '../../components/16pxIcons/ScanIcon16';
import { logEvent } from '../../components/LogEvent';
import getAsset from '../../requests/Assets/GetAsset';
import CloseModalIcon from '../../components/CloseModalIcon';
import getAssets, { AssetSortCategory, GetAssetsProps } from '../../requests/Assets/GetAssets';
import NfcIcon24 from '../../components/24pxIcons/NfcIcon24';
import NfcIcon from '../../components/30pxIcons/NfcIcon';
import {
  AssetFilterCategory, AssetFilterSelection,
} from './components/AssetFilterModal/components/AssetFilterItem';
import { RootTabScreenProps } from '../../types';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import ColumnsButton from '../../components/ColumnsButton';
import ColumnSelectionPane from './components/ColumnSelectionPane';
import ScreenEmptyState from '../../components/ScreenEmptyState';
import NoAssetsImage from '../../components/AssetManagement/NoAssetsImage';
import { resetAllAssetFilters } from '../../redux/assetsFilterSlice';
import AssetFilterTagSection from './components/AssetFilterTagSection';
import { Area, Floor, Space } from '../../components/Map/SetFloorsModal/FloorItem';
import { checkIfFiltersApplied } from './Utils';
import { DELETE_ASSET_SUCCESS } from '../../constants/AlertMessages';

const AssetManagementScreen = ({ route }: RootTabScreenProps<AppScreen.ASSET_MANAGEMENT>) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const happyEnabled: boolean = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canAddAsset: boolean = isMobile
    && (isAdmin || permissions?.includes(UserPermission.ASSETS_CREATE));
  const canViewAssets: boolean = isAdmin || permissions?.includes(UserPermission.ASSETS_READ);
  const filterSelectionSaved: boolean = useSelector((state:
    { assetsFilter: { saved: boolean } }) => state.assetsFilter.saved);
  const savedFilters: AssetFilterSelection[] = useSelector((state:
    { assetsFilter: { filters: AssetFilterSelection[] } }) => state.assetsFilter.filters);
  const defaultAssetsFilter: AssetFilterSelection[] = isMobile ? [] : [{ category: null }];
  const [templateType, setTemplateType] = useState<TemplateType>(TemplateType.ASSET);
  const [templates, setTemplates] = useState<object>({});
  const [assets, setAssets] = useState<AssetInstance[] | undefined | null>(undefined);
  const [assetFilterShown, setAssetFilterShown] = useState<boolean>(false);
  const [assetFilterResults,
    setAssetFilterResults] = useState<AssetInstance[] | undefined | null>(null);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [selectedAsset, setSelectedAsset] = useState<AssetInstance>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [tooltipIconHovered, setTooltipIconHovered] = useState<boolean>(false);
  const [tooltipHovered, setTooltipHovered] = useState<boolean>(false);
  const [addAssetModalVisible, setAddAssetModalVisible] = useState<boolean>(false);
  const [scanned, setScanned] = useState<boolean>(true);
  const [status, requestPermission] = useCameraPermissions();
  const [allAssetsCount, setAllAssetsCount] = useState<number>(0);
  const [filteredAssetsCount, setFilteredAssetsCount] = useState<number>(0);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(0);
  const [sortDescendingSelected, setSortDescendingSelected] = useState<boolean>(false);
  const [sortDescendingApplied,
    setSortDescendingApplied] = useState<boolean>(sortDescendingSelected);
  const [initialScrollIndex, setInitialScrollIndex] = useState<number>(0);
  const [selectedSortCategory,
    setSelectedSortCategory] = useState<AssetSortCategory>(AssetSortCategory.NAME);
  const [appliedSortCategory,
    setAppliedSortCategory] = useState<AssetSortCategory>(selectedSortCategory);
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);
  const [selectedSpace, setSelectedSpace] = useState<Space | null>(null);
  const [appliedFloor, setAppliedFloor] = useState<Floor | null>(null);
  const [appliedArea, setAppliedArea] = useState<Area | null>(null);
  const [appliedSpace, setAppliedSpace] = useState<Space | null>(null);
  const selectedLocationId: string = selectedSpace?.LocationId ?? selectedArea?.LocationId
    ?? selectedFloor?.LocationId ?? selectedBuildingId;
  const [nfcPressed, setNfcPressed] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<AssetFilterSelection[]>(
    filterSelectionSaved ? savedFilters : defaultAssetsFilter,
  );
  const [appliedFilters, setAppliedFilters] = useState<AssetFilterSelection[]>(
    filterSelectionSaved ? savedFilters : defaultAssetsFilter,
  );
  const tooltipShown: boolean = tooltipHovered || tooltipIconHovered;
  const safeAreaInsets = useSafeAreaInsets();
  const [columnSelectionOpened, setColumnSelectionOpened] = useState<boolean>(false);
  const [imageShown, setImageShown] = useState<boolean>(false);
  const [idShown, setIdShown] = useState<boolean>(true);
  const [templateShown, setTemplateShown] = useState<boolean>(true);
  const [locationShown, setLocationShown] = useState<boolean>(true);
  const [modelShown, setModelShown] = useState<boolean>(false);
  const [manufacturerShown, setManufacturerShown] = useState<boolean>(false);
  const [serialShown, setSerialShown] = useState<boolean>(false);
  const [nameShown] = useState<boolean>(true);
  const templateTabSelectionDisabled: boolean = !!appliedFilters.find((filter:
    AssetFilterSelection) => filter.category === AssetFilterCategory.TEMPLATE)?.value;

  const loadTemplates: () => void = () => {
    getTemplateGroups().then((response) => {
      if (!response) return;
      setTemplates(response?.groups
        ?.reduce((prev, group) => {
          group.buttonPrototypes?.forEach((template: Template) => {
            prev[template.id] = template;
          });
          return prev;
        }, {}) ?? {});
    });
  };

  const loadData = (pageNumber: number = 0) => {
    if (!canViewAssets || loading) return;
    setLoading(true);
    getAssets({
      locationId: selectedBuildingId,
      templateType,
      enableSubLocationsLookUp: true,
      pageNumber,
      pageSize,
      textSearch: searchValue,
      sortBy: selectedSortCategory,
      sortDescending: sortDescendingSelected,
    }).then((result) => {
      setAllAssetsCount(result.count);
      const assetList: AssetInstance[] | null = result.assets ?? null;
      const updatedAssetList: AssetInstance[] = (pageNumber && assetList)
        ? [...(assets ?? []), ...assetList] : assetList;
      setAssets(updatedAssetList);
      setAppliedSortCategory(selectedSortCategory);
      setSortDescendingApplied(sortDescendingSelected);
    }).catch(() => {
      if (!(assets?.length > 0)) setAssets(null);
    }).finally(() => {
      setLoading(false);
    });
  };

  const refreshData: () => void = () => {
    if (checkIfFiltersApplied(appliedFilters) || !!appliedFloor) applyFilters();
    else {
      setCurrentPageNumber(0);
      if (!(selectedBuildingId && pageSize && !loading)) return;
      setAssets(undefined);
      loadData();
    }
  };

  const loadNextPage: () => void = () => {
    if (loading || !(assets?.length && assets?.length < allAssetsCount)
      || (assetFilterResults && assetFilterResults.length >= filteredAssetsCount)) return;
    setCurrentPageNumber(currentPageNumber + 1);
    loadData(currentPageNumber + 1);
  };

  const decodePayload = (payload: number[]): string => {
    const filteredPayload = payload.slice(1);
    return String.fromCharCode(...filteredPayload);
  };

  useEffect(() => {
    if (!isMobile) refreshData();
  }, [selectedBuildingId, pageSize, templateType, searchValue, canViewAssets,
    sortDescendingSelected, selectedSortCategory]);

  useEffect(() => {
    if (isMobile) refreshData();
  }, [selectedBuildingId, pageSize, templateType, searchValue, canViewAssets]);

  useEffect(() => setColumnSelectionOpened(false), [
    selectedAsset, selectedSortCategory, sortDescendingSelected, searchValue, assetFilterShown,
    templateType, currentPageNumber]);

  useEffect(() => {
    if (filterSelectionSaved) applyFilters();
    loadTemplates();
    if (!isMobile) return undefined;
    if (!status || (!status?.granted && status?.canAskAgain)) requestPermission();
    const unsubscribe = navigation.addListener('focus', loadTemplates);
    return unsubscribe;
  }, []);

  useEffect(() => {
    const templateId: string = (route?.params as any)?.templateId;
    const templateName: string = (route?.params as any)?.templateName;
    if (!templateId) return;
    const filters: AssetFilterSelection[] = [{
      category: AssetFilterCategory.TEMPLATE,
      value: templateId,
      displayValue: templateName ?? templates[templateId]?.name ?? '',
    }];
    setSelectedAsset(null);
    setSelectedFilters(filters);
    applyFilters(filters);
    navigation.setParams({ templateId: undefined, templateName: undefined } as any);
  }, [route]);

  useEffect(() => {
    if (currentPageNumber === 0 && assets?.length > 0 && assets.length < pageSize
      && assets.length < allAssetsCount) {
      loadNextPage();
    }
  }, [assets]);

  const showScreenAlert = (
    title: string,
    message: string,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string | null = null,
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setAlertVisible(true);
  };

  const handleBarCodeScanned = ({ data }) => {
    setScanned(true);
    const scanValue = data.split('.tech/')[1]?.split('?')[0];
    getAsset(scanValue).then((res: AssetInstance | null) => {
      if (res && res.id) setSelectedAsset(res);
      else showScreenAlert('Error', 'No asset associated with this Solve tag');
    });
  };

  const scanner = () => {
    if (!status?.granted && status?.canAskAgain) {
      return <Text>{'Requesting for camera permission'}</Text>;
    }
    if (!status?.granted) return <Text>{'No access to camera'}</Text>;

    return <CameraView
      onBarcodeScanned={scanned ? undefined : handleBarCodeScanned}
      style={StyleSheet.absoluteFillObject}
    />;
  };

  const deleteAsset = (assetId: string) => {
    setAssets(assets?.filter((a: AssetInstance) => a.id !== assetId));
    setAssetFilterResults(assetFilterResults?.filter((a: AssetInstance) => a.id !== assetId));
    setAllAssetsCount(Math.max(0, allAssetsCount - 1));
    setSelectedAsset(null);
    if (isMobile) showScreenAlert('Success', DELETE_ASSET_SUCCESS, false);
  };

  const updateSelectedAsset = (updatedAsset: AssetInstance) => {
    setSelectedAsset(updatedAsset);
    setAssets(assets
      ?.map((asset: AssetInstance) => (asset.id === updatedAsset.id ? updatedAsset : asset)));
    setAssetFilterResults(assetFilterResults
      ?.map((asset: AssetInstance) => (asset.id === updatedAsset.id ? updatedAsset : asset)));
  };

  const hideAssetFilter: () => void = () => {
    setAssetFilterShown(false);
    setSelectedFilters([...appliedFilters]);
  };

  const resetAssetFilter: () => void = () => {
    if (isMobile) setAssetFilterShown(false);
    setAssetFilterResults(undefined);
    setSelectedFilters([...defaultAssetsFilter]);
    setAppliedFilters([...defaultAssetsFilter]);
    dispatch(resetAllAssetFilters());
    setSelectedFloor(null);
    setSelectedArea(null);
    setSelectedSpace(null);
    setAppliedFloor(null);
    setAppliedArea(null);
    setAppliedSpace(null);
    setAssets(undefined);
    setCurrentPageNumber(0);
    loadData(0);
  };

  const applyFilters = (
    filters?: AssetFilterSelection[],
    floor?: Floor,
    area?: Area,
    space?: Space,
  ) => {
    if (!canViewAssets || loading) return;
    const applicableFilters: AssetFilterSelection[] = filters ?? selectedFilters;
    if (!(checkIfFiltersApplied(applicableFilters) || !!selectedFloor || !!floor)) {
      resetAssetFilter();
      return;
    }
    setLoading(true);
    let locationId: string = selectedBuildingId;
    if (floor === undefined) locationId = selectedLocationId;
    if (floor) locationId = floor.LocationId;
    if (area) locationId = area.LocationId;
    if (space) locationId = space.LocationId;
    const requestBody: GetAssetsProps = {
      enableSubLocationsLookUp: true,
      locationId,
      sortBy: selectedSortCategory,
      textSearch: searchValue,
      sortDescending: sortDescendingSelected,
      templateType,
    };
    applicableFilters.forEach((filter: AssetFilterSelection) => {
      if (filter.category === AssetFilterCategory.DESCRIPTION) {
        requestBody.description = filter.value;
      }
      if (filter.category === AssetFilterCategory.MANUFACTURER) {
        requestBody.manufacturer = filter.value;
      }
      if (filter.category === AssetFilterCategory.MODEL) requestBody.model = filter.value;
      if (filter.category === AssetFilterCategory.SERIAL_NUMBER) requestBody.serial = filter.value;
      if (filter.category === AssetFilterCategory.NAME) requestBody.name = filter.value;
      if (filter.category === AssetFilterCategory.TEMPLATE) requestBody.templateId = filter.value;
      if (filter.category === AssetFilterCategory.ID) requestBody.assetId = filter.value;
    });
    if (requestBody.templateId) {
      requestBody.templateType = templates[requestBody.templateId]?.type;
      setTemplateType(requestBody.templateType);
    }
    getAssets(requestBody).then((res) => {
      if (res?.assets) {
        setAssetFilterResults(res.assets);
        setFilteredAssetsCount(res.count);
        setAppliedFilters(applicableFilters);
        setAppliedFloor(floor === null ? null : selectedFloor);
        setAppliedArea(area === null ? null : selectedArea);
        setAppliedSpace(space === null ? null : selectedSpace);
        setAppliedSortCategory(selectedSortCategory);
        setSortDescendingApplied(sortDescendingSelected);
        hideAssetFilter();
      } else showScreenAlert('Error', 'No filter results found');
    }).catch(() => {
      showScreenAlert('Error', 'Unable to load filter results at the moment');
    }).finally(() => setLoading(false));
  };

  return <SafeAreaView style={styles.container}>
    {canViewAssets ? <>
      {!selectedAsset && <View style={[styles.header, { maxWidth: screenMaxWidth - 16 }]}>
        <Text style={[styles.title, selectedAsset && { flex: 1 }]}>
          {'Asset management'}
        </Text>
        {!isMobile && <Pressable
          onHoverIn={() => setTooltipIconHovered(true)}
          onHoverOut={() => setTooltipIconHovered(false)}
          style={({ hovered }: PressableState) => hovered && Style.hovered}
        >
          <InfoIcon color={Colors.light.hereworksBlue400} />
        </Pressable>}
        <View
          onPointerEnter={() => setTooltipHovered(true)}
          onPointerLeave={() => setTooltipHovered(false)}
          style={styles.tooltipContainer}
        >
          {tooltipShown && <View style={styles.tooltip}>
            <Text style={styles.tooltipText}>
              {'Asset addition is not supported on this device. Please use your phone to add an asset. '}
            </Text>
          </View>}
        </View>
      </View>}
      <View style={[isMobile ? { flex: 1 } : styles.content, { maxWidth: screenMaxWidth - 74 }]}>
        <View style={styles.tabSelectionHeader}>
          {!isMobile && <Text style={styles.subtitle}>
            {'Assets'}
          </Text>}
          {happyEnabled && (isMobile ? <TabSelectionMobile
            selectOption={setTemplateType}
            selectedOption={templateType}
            options={Object.values(TemplateType)}
            titles={['ASSETS', 'SENSORS', 'METERS']}
            disabled={templateTabSelectionDisabled}
          /> : <View style={Style.row}>
            {Object.values(TemplateType).map((option: TemplateType) => <TabButton
              key={`asset_management_tab_selection_${option}`}
              title={`${option}S`}
              select={() => setTemplateType(option)}
              selected={templateType === option}
              disabled={templateTabSelectionDisabled}
            />)}
          </View>)}
        </View>
        <View style={styles.tabSelectionHeader}>
          <View style={Style.row}>
            {isMobile && <Button
              onPress={async () => {
                logEvent('AssetMgmtScreenMobile', 'nfc_icon');

                if (Platform.OS === 'android') setNfcPressed(true);
                try {
                  await NfcManager.start();
                  await NfcManager.requestTechnology(NfcTech.Ndef);
                  const tag = await NfcManager.getTag();
                  NfcManager.cancelTechnologyRequest();
                  const decodedTag = decodePayload(tag.ndefMessage[0].payload);
                  setScanned(true);
                  if (decodedTag) handleBarCodeScanned({ data: decodedTag });
                  // handleBarCodeScanned({ data: 'https://dev.hereworks.tech/asset00492?ss=QR' });
                } finally {
                  setNfcPressed(false);
                  NfcManager.cancelTechnologyRequest();
                }
              }}
              children={<NfcIcon24 />}
              style={{ ...styles.button, ...styles.filterButton }}
              noShadow
            />}
            {isMobile && <Button
              onPress={() => {
                setScanned(!scanned);
                logEvent('AssetMgmtScreenMobile', 'scan_icon');
              }}
              children={<ScanIcon16 />}
              style={{ ...styles.button, ...styles.filterButton }}
              noShadow
            />}
            <Button
              title={!isMobile && 'Filters'}
              onPress={() => setAssetFilterShown(true)}
              children={<FilterIcon16 style={!isMobile && styles.filterIcon} />}
              style={{ ...styles.button, ...styles.filterButton }}
              noShadow
            />
            {!isMobile && assetFilterResults && <Button
              title='Reset filter'
              style={styles.button}
              onPress={resetAssetFilter}
            />}
          </View>
          <View style={styles.searchBarContainer}>
            <SearchBar
              apply={setSearchValue}
              {...{ loading }}
              placeholder={isMobile ? undefined : 'Search by asset name, ID, location, custom field'}
              style={styles.searchBar}
            />
            {!isMobile && <ColumnsButton
              onPress={() => setColumnSelectionOpened(!columnSelectionOpened)}
            />}
          </View>
        </View>
        <AssetFilterTagSection
          {...{
            selectedFilters,
            setSelectedFilters,
            appliedFilters,
            applyFilters,
            appliedFloor,
            setSelectedFloor,
            appliedArea,
            setSelectedArea,
            appliedSpace,
            setSelectedSpace,
            resetAssetFilter,
          }}
        />
        {!isMobile && <View style={{ zIndex: 2 }}>
          {columnSelectionOpened && <ColumnSelectionPane
            {...{
              imageShown,
              setImageShown,
              idShown,
              setIdShown,
              locationShown,
              setLocationShown,
              templateShown,
              setTemplateShown,
              modelShown,
              setModelShown,
              manufacturerShown,
              setManufacturerShown,
              serialShown,
              setSerialShown,
              nameShown,
            }}
          />}
        </View>}
        <AssetList
          assets={assetFilterResults ?? assets}
          {...{
            setSelectedAsset,
            setAssets,
            pageSize,
            setPageSize,
            loading,
            loadNextPage,
            initialScrollIndex,
            setInitialScrollIndex,
            imageShown,
            idShown,
            templateShown,
            locationShown,
            modelShown,
            manufacturerShown,
            serialShown,
            selectedSortCategory,
            setSelectedSortCategory,
            searchValue,
            sortDescendingSelected,
            setSortDescendingSelected,
          }}
          count={assetFilterResults ? filteredAssetsCount : allAssetsCount}
        />
        {canAddAsset && <Button
          children={<PlusIcon style={styles.plusIcon} />}
          title='Add asset'
          style={styles.addAssetButton}
          affirmative
          onPress={() => setAddAssetModalVisible(true)}
        />}
      </View>
      {selectedAsset && <View
        style={[styles.selectedAssetContainer, isMobile && { top: safeAreaInsets.top }]}
      >
        <SelectedAsset
          asset={selectedAsset}
          template={templates[selectedAsset?.templateId]}
          {...{
            showScreenAlert,
            loading,
            setLoading,
            setSelectedAsset,
            deleteAsset,
            updateSelectedAsset,
          }}
        />
      </View>}
      <AssetFilterModal
        {...{
          assetFilterShown,
          assets,
          assetFilterResults,
          setAssetFilterResults,
          selectedSortCategory,
          setSelectedSortCategory,
          sortDescendingSelected,
          setSortDescendingSelected,
          selectedFilters,
          setSelectedFilters,
          loading,
          selectedLocationId,
          selectedFloor,
          setSelectedFloor,
          selectedArea,
          setSelectedArea,
          selectedSpace,
          setSelectedSpace,
          appliedFilters,
          resetAssetFilter,
        }}
        templates={Object.values(templates)}
        hide={hideAssetFilter}
        resetOnHide={() => {
          if (isMobile) {
            setSelectedSortCategory(appliedSortCategory);
            setSortDescendingSelected(sortDescendingApplied);
          }
        }}
        apply={() => applyFilters()}
      />
      <AlertModal
        title={alertTitle}
        message={alertMessage}
        error={alertError}
        modalVisible={alertVisible}
        action={alertAction}
        actionTitle={alertActionTitle}
        setModalVisible={setAlertVisible}
      />
      {isMobile && <AddAssetModal
        visible={addAssetModalVisible}
        setModalVisible={setAddAssetModalVisible}
        template={templates[0]}
        addAsset={refreshData}
      />}
      <Modal
        isVisible={!scanned}
        style={{ alignItems: 'center', margin: 0 }}
        onSwipeComplete={() => setScanned(true)}
        swipeDirection={'down'}
        animationIn={'fadeIn'}
        animationOut={'fadeOut'}
      >
        {scanner()}
        <PressableStyled
          onPress={() => setScanned(true)}
          style={[Style.closeButton, { top: safeAreaInsets.top ?? 32 }]}
        >
          <CloseModalIcon />
        </PressableStyled>
      </Modal>
      <Modal isVisible={nfcPressed} style={{ justifyContent: 'flex-end', margin: 0 }}>
        <View style={{
          width: '100%',
          backgroundColor: '#FFF',
          paddingBottom: 32,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          paddingHorizontal: 12,
          alignItems: 'center',
        }}>
          <Text style={styles.scanSubtitle}>Approach tag</Text>
          <View style={{ paddingVertical: 12 }}>
            <NfcIcon />
          </View>
          <Button style={{ flex: undefined, alignSelf: 'stretch', marginTop: 8 }} title='Cancel NFC Scan' onPress={() => {
            setNfcPressed(false);
            NfcManager.cancelTechnologyRequest();
          }} />
        </View>
      </Modal>
    </> : <ScreenEmptyState
      title='No permission to view assets'
      subtitle='You do not have the required permission to view assets. Please contact your administrator.'
      image={<NoAssetsImage />}
    />}
  </SafeAreaView>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: isMobile ? undefined : 48,
    paddingHorizontal: isMobile ? 24 : 40,
    paddingBottom: isMobile ? undefined : 24,
    backgroundColor: Colors.light.hereworksBackground,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: isMobile ? 24 : 32,
  },
  title: {
    fontSize: isMobile ? 22 : 32,
    lineHeight: isMobile ? 33 : 48,
    fontFamily: isMobile ? 'Poppins_600SemiBold' : 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginEnd: 16,
  },
  content: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue50,
    backgroundColor: '#FFF',
    flex: 1,
  },
  subtitle: {
    fontSize: 20,
    lineHeight: 32,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
  },
  tabSelectionHeader: {
    ...Style.row,
    marginBottom: isMobile ? 16 : 24,
    flexDirection: isMobile ? 'row-reverse' : 'row',
  },
  searchBarContainer: {
    ...Style.row,
    flex: isMobile ? 1 : undefined,
  },
  filterIcon: {
    marginStart: 8,
  },
  button: {
    height: 32,
    aspectRatio: isMobile ? 1 : undefined,
    marginEnd: isMobile ? undefined : 8,
    marginStart: isMobile ? 8 : undefined,
    flex: undefined,
  },
  filterButton: {
    borderColor: Colors.light.hereworksBlue100,
  },
  backButton: {
    borderRadius: 100,
    borderWidth: 1,
    backgroundColor: '#FFF',
    borderColor: Colors.light.hereworksBlue100,
    width: 36,
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginEnd: 24,
  },
  tooltipContainer: {
    paddingStart: 8,
    height: '100%',
  },
  tooltip: {
    borderRadius: 5,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: '#FFF',
    width: 231,
    minHeight: 56,
    position: 'absolute',
  },
  tooltipText: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Poppins_500Medium',
    letterSpacing: 0.15,
    color: Colors.light.hereworksBlue400,
  },
  addAssetButton: {
    flexDirection: 'row-reverse',
    flex: undefined,
    marginBottom: Platform.OS === 'android' ? 16 : undefined,
  },
  plusIcon: {
    marginEnd: 8,
  },
  scanSubtitle: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
    lineHeight: 20,
    marginTop: 20,
    color: Colors.light.hereworksBlack600,
    textAlign: 'center',
  },
  searchBar: isMobile ? {} : {
    maxWidth: 400,
    marginEnd: 8,
  },
  selectedAssetContainer: isMobile ? {
    position: 'absolute',
    backgroundColor: Colors.light.hereworksBackground,
    paddingHorizontal: 16,
    top: 0,
    start: 0,
    end: 0,
    height: '100%',
  } : {
    position: 'absolute',
    backgroundColor: Colors.light.hereworksBackground,
    width: '100%',
    height: '100%',
    paddingEnd: 80,
    paddingBottom: 72,
  },
});

export default AssetManagementScreen;
